import { Modal, Button, Form } from "react-bootstrap";
import { useEffect, useState } from "react";
import { fetchWithAuth } from "../api/fetchWithAuth";

const categoryOptions = [
  { value: "", label: "none" },
  { value: "informative", label: "informative" },
  { value: "definition", label: "definition" },
  { value: "others", label: "others" }
];

const sectionCategoryOptions = [
  { value: "section", label: "section" },
  { value: "others_sec", label: "others_sec" }
];

const isSectionCategory = (value) => ["section", "others_sec"].includes(String(value ?? "").trim());

function EraRequirementFormModal({
  show,
  onClose,
  onSaved,
  requirement,
  apiBase = "/api/era-requirements",
  showExtendedFields = false
}) {
  const isEdit = Boolean(requirement);
  const [form, setForm] = useState({
    req_number: "",
    req_title: "",
    dmi: false,
    onboard: false,
    trackside: false,
    category: ""
  });
  const [saving, setSaving] = useState(false);
  const lockedSectionCategory = isEdit && isSectionCategory(requirement?.category);
  const visibleCategoryOptions = lockedSectionCategory
    ? [...categoryOptions, ...sectionCategoryOptions]
    : categoryOptions;

  useEffect(() => {
    if (!requirement) {
      setForm({
        req_number: "",
        req_title: "",
        dmi: false,
        onboard: false,
        trackside: false,
        category: ""
      });
      return;
    }

    setForm({
      req_number: requirement.req_number ?? "",
      req_title: requirement.req_title ?? "",
      dmi: Boolean(requirement.dmi),
      onboard: Boolean(requirement.onboard),
      trackside: Boolean(requirement.trackside),
      category: requirement.category ?? ""
    });
  }, [requirement, show]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;

    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();
    if (saving) return;

    setSaving(true);

    try {
      const body = {
        req_number: form.req_number.trim(),
        req_title: form.req_title.trim(),
        dmi: form.dmi
      };

      if (showExtendedFields) {
        body.onboard = form.onboard;
        body.trackside = form.trackside;
        if (!lockedSectionCategory) {
          body.category = form.category || null;
        }
      }

      const res = await fetchWithAuth(isEdit ? `${apiBase}/${requirement.id}` : apiBase, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const error = await res.json().catch(() => null);
        alert(error?.error || "Save failed");
        return;
      }

      await onSaved?.();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? "Edit ERA Requirement" : "Add ERA Requirement"}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Number</Form.Label>
            <Form.Control
              name="req_number"
              value={form.req_number}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="req_title"
              value={form.req_title}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Check
            type="checkbox"
            id="era-requirement-dmi"
            name="dmi"
            label="DMI"
            checked={form.dmi}
            onChange={handleChange}
          />

          {showExtendedFields && (
            <>
              <Form.Check
                className="mt-2"
                type="checkbox"
                id="era-requirement-onboard"
                name="onboard"
                label="Onboard"
                checked={form.onboard}
                onChange={handleChange}
              />

              <Form.Check
                className="mt-2 mb-3"
                type="checkbox"
                id="era-requirement-trackside"
                name="trackside"
                label="Trackside"
                checked={form.trackside}
                onChange={handleChange}
              />

              <Form.Group>
                <Form.Label>Category</Form.Label>
                <Form.Select
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  disabled={lockedSectionCategory}
                >
                  {visibleCategoryOptions.map((option) => (
                    <option key={option.value || "empty"} value={option.value}>{option.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>

        <Button variant="success" onClick={handleSubmit} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EraRequirementFormModal;

